import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { showSuccessSnackbar, showWarningSnackbar } from '../../../snackBar/snakbar';
import { LogoutStart } from '../../../../redux/reducers/login';
import { createGoogleUserPasswordApi, type CreateGoogleUserPasswordErrorData } from '../api/createGoogleUserPassword.api';
import { createGoogleUserPasswordSchema, type CreateGoogleUserPasswordFormValues } from '../data/createGoogleUserPasswordSchema';
import type { UseCreateGoogleUserPasswordParams, UseCreateGoogleUserPasswordResult } from '../types';

const INITIAL_VALUES: CreateGoogleUserPasswordFormValues = {
  newPassword: '',
  confirmPassword: '',
};

export const useCreateGoogleUserPassword = ({ isOpen, onClose }: UseCreateGoogleUserPasswordParams): UseCreateGoogleUserPasswordResult => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const formMethods = useForm<CreateGoogleUserPasswordFormValues>({
    mode: 'onChange',
    resolver: zodResolver(createGoogleUserPasswordSchema),
    defaultValues: INITIAL_VALUES,
  });
  const { handleSubmit: createSubmitHandler, reset, setFocus } = formMethods;
  const username = localStorage?.getItem('email') ?? '';

  const handleSignout = () => {
    dispatch(LogoutStart());
    navigate('/signout', { replace: true });
  };

  const handleClose = () => {
    onClose();
  };

  const createGoogleUserPassword = async (newPassword: string) => {
    try {
      setIsLoading(true);

      const response = await createGoogleUserPasswordApi({ password: newPassword });
      const responseData = response.data;

      showSuccessSnackbar( 'Google user password created successfully');
      handleSignout();
    } catch (err) {
      const fallbackMessage = 'Failed to create Google user password. Please try again.';

      if (!axios.isAxiosError<CreateGoogleUserPasswordErrorData>(err)) {
        showWarningSnackbar(fallbackMessage);
        return;
      }

      const apiMessage = err.response?.data?.message || err.response?.data?.error || err.response?.data?.errorMessage;

      // Prefer API message; never surface raw axios status text (e.g. "Request failed with status code 404").
      showWarningSnackbar(apiMessage || fallbackMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const submit = createSubmitHandler(async ({ newPassword }) => {
    await createGoogleUserPassword(newPassword);
  });

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    void submit();
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setFocus('newPassword'), 100);
    } else {
      reset(INITIAL_VALUES);
    }
  }, [isOpen, reset, setFocus]);

  useEffect(() => {
    const handleCtrlS = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void submit();
      }
    };

    window.addEventListener('keydown', handleCtrlS);
    return () => {
      window.removeEventListener('keydown', handleCtrlS);
    };
  }, [submit]);

  return {
    formMethods,
    isLoading,
    username,
    handleSubmit,
    handleClose,
  };
};
